import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { ContractDto } from "@rentablo/api/contracts";
import { ContractEditInitialMode } from "@features/contractAnalyzer/types";

interface OpenEditModalOptions {
  initialMode?: ContractEditInitialMode;
  splitTransactions?: string[];
}

export interface ContractEditTarget {
  contract: ContractDto;
  initialMode: ContractEditInitialMode;
  splitTransactions: string[];
}

interface ContractAnalyzerContextProps {
  expandedContract?: number;
  setExpandedContract: (contractId?: number) => void;
  editTarget?: ContractEditTarget;
  openEditModal: (
    contract: ContractDto,
    options?: OpenEditModalOptions
  ) => void;
  closeEditModal: () => void;
}

const ContractAnalyzerContext = createContext<
  ContractAnalyzerContextProps | undefined
>(undefined);

export const useContractAnalyzerContext = (): ContractAnalyzerContextProps => {
  const context = useContext(ContractAnalyzerContext);
  if (!context) {
    throw new Error(
      "useContractAnalyzerContext must be used within a ContractAnalyzerProvider."
    );
  }
  return context;
};

export const ContractAnalyzerProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [expandedContract, setExpandedContract] = useState<number>();
  const [editTarget, setEditTarget] = useState<ContractEditTarget>();

  const openEditModal = useCallback(
    (contract: ContractDto, options?: OpenEditModalOptions) => {
      setEditTarget({
        contract,
        initialMode: options?.initialMode ?? "basic",
        splitTransactions: options?.splitTransactions ?? [],
      });
    },
    []
  );

  const closeEditModal = useCallback(() => {
    setEditTarget(undefined);
  }, []);

  return (
    <ContractAnalyzerContext.Provider
      value={{
        expandedContract,
        setExpandedContract,
        editTarget,
        openEditModal,
        closeEditModal,
      }}
    >
      {children}
    </ContractAnalyzerContext.Provider>
  );
};
